import React, { useState } from 'react';
import { Button } from '../common';

const styles = {
  section: {
    position: 'relative',
    zIndex: 1,
    padding: '120px 24px',
    overflow: 'hidden',
  },
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, transparent 0%, rgba(59, 130, 246, 0.03) 50%, transparent 100%)',
    pointerEvents: 'none',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'center',
  },
  left: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontSize: 'clamp(32px, 4vw, 48px)',
    fontWeight: '700',
    fontFamily: "'Space Grotesk', sans-serif",
    color: '#fff',
    marginBottom: '20px',
    lineHeight: '1.1',
  },
  titleGradient: {
    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%)',
    backgroundSize: '200% 200%',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'gradient 5s ease infinite',
  },
  subtitle: {
    fontSize: '18px',
    color: '#94a3b8',
    lineHeight: '1.7',
    marginBottom: '40px',
  },
  steps: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    marginBottom: '40px',
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  stepIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
    border: '1px solid rgba(59, 130, 246, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  stepContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  stepTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#fff',
  },
  stepDesc: {
    fontSize: '14px',
    color: '#64748b',
  },
  trust: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    paddingTop: '32px',
    borderTop: '1px solid rgba(255, 255, 255, 0.05)',
  },
  trustItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#64748b',
  },
  trustIcon: {
    color: '#34d399',
  },
  formCard: {
    padding: '48px',
    borderRadius: '24px',
    position: 'relative',
  },
  formTitle: {
    fontSize: '24px',
    fontWeight: '700',
    fontFamily: "'Space Grotesk', sans-serif",
    color: '#fff',
    marginBottom: '8px',
  },
  formSubtitle: {
    fontSize: '15px',
    color: '#64748b',
    marginBottom: '32px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#e2e8f0',
  },
  input: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '12px',
    padding: '14px 18px',
    fontSize: '15px',
    color: '#fff',
    outline: 'none',
    transition: 'all 0.2s ease',
    fontFamily: "'DM Sans', sans-serif",
  },
  select: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '12px',
    padding: '14px 18px',
    fontSize: '15px',
    color: '#fff',
    outline: 'none',
    transition: 'all 0.2s ease',
    fontFamily: "'DM Sans', sans-serif",
    cursor: 'pointer',
  },
  textarea: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '12px',
    padding: '14px 18px',
    fontSize: '15px',
    color: '#fff',
    outline: 'none',
    transition: 'all 0.2s ease',
    fontFamily: "'DM Sans', sans-serif",
    resize: 'none',
    minHeight: '100px',
  },
  hidden: {
    display: 'none',
  },
  successMessage: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  successIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(52, 211, 153, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    fontSize: '40px',
  },
  successTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#fff',
    marginBottom: '12px',
    fontFamily: "'Space Grotesk', sans-serif",
  },
  successText: {
    fontSize: '16px',
    color: '#94a3b8',
    lineHeight: '1.6',
  },
};

const ctaSteps = [
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
    title: 'Korte intake van 30 min',
    desc: 'We bespreken je situatie',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
    ),
    title: 'Gratis CRM-scan',
    desc: 'We analyseren je data',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
      </svg>
    ),
    title: 'Vaste prijs, geen verrassingen',
    desc: 'Transparante offerte',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
    ),
    title: 'Binnen 2 weken van start',
    desc: 'Snel resultaat',
  },
];

const crmOptions = [
  { value: '', label: 'Selecteer je CRM...' },
  { value: 'hubspot', label: 'HubSpot' },
  { value: 'salesforce', label: 'Salesforce' },
  { value: 'dynamics', label: 'Microsoft Dynamics' },
  { value: 'pipedrive', label: 'Pipedrive' },
  { value: 'anders', label: 'Anders' },
];

export const CTA = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);

    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData).toString(),
    })
      .then(() => setIsSubmitted(true))
      .catch((error) => console.error('Error:', error));
  };

  return (
    <section id="contact" style={styles.section}>
      <div style={styles.background} />
      <div style={styles.container} className="cta-container">
        {/* Left side - Info */}
        <div style={styles.left}>
          <h2 style={styles.title}>
            Klaar voor <br />
            <span style={styles.titleGradient}>schone CRM data?</span>
          </h2>
          <p style={styles.subtitle}>
            Geen lange trajecten. Geen vage beloftes.
            Plan een korte intake en ontdek wat we voor jouw CRM kunnen betekenen.
          </p>

          <div style={styles.steps}>
            {ctaSteps.map((step, i) => (
              <div key={i} style={styles.step}>
                <div style={styles.stepIcon}>
                  {step.icon}
                </div>
                <div style={styles.stepContent}>
                  <span style={styles.stepTitle}>{step.title}</span>
                  <span style={styles.stepDesc}>{step.desc}</span>
                </div>
              </div>
            ))}
          </div>

          <div style={styles.trust}>
            <div style={styles.trustItem}>
              <span style={styles.trustIcon}>✓</span>
              Geen verplichtingen
            </div>
            <div style={styles.trustItem}>
              <span style={styles.trustIcon}>✓</span>
              Reactie binnen 24u
            </div>
            <div style={styles.trustItem}>
              <span style={styles.trustIcon}>✓</span>
              100% AVG-proof
            </div>
          </div>
        </div>

        {/* Right side - Form */}
        <div style={styles.formCard} className="card-premium card-premium-highlighted">
          {!isSubmitted ? (
            <>
              <h3 style={styles.formTitle}>Plan je intake gesprek</h3>
              <p style={styles.formSubtitle}>
                Vul het formulier in en we nemen binnen 24 uur contact op.
              </p>
              <form
                name="intake"
                method="POST"
                data-netlify="true"
                netlify-honeypot="bot-field"
                style={styles.form}
                onSubmit={handleSubmit}
              >
                <input type="hidden" name="form-name" value="intake" />
                <p style={styles.hidden}>
                  <label>Don't fill this out: <input name="bot-field" /></label>
                </p>

                <div style={styles.formRow} className="form-row">
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Naam *</label>
                    <input
                      type="text"
                      name="naam"
                      required
                      placeholder="Je naam"
                      style={styles.input}
                      className="form-input"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Bedrijf *</label>
                    <input
                      type="text"
                      name="bedrijf"
                      required
                      placeholder="Bedrijfsnaam"
                      style={styles.input}
                      className="form-input"
                    />
                  </div>
                </div>

                <div style={styles.formRow} className="form-row">
                  <div style={styles.formGroup}>
                    <label style={styles.label}>E-mail *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="je@bedrijf.nl"
                      style={styles.input}
                      className="form-input"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Telefoon</label>
                    <input
                      type="tel"
                      name="telefoon"
                      placeholder="+31 6..."
                      style={styles.input}
                      className="form-input"
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Welk CRM gebruiken jullie?</label>
                  <select name="crm" style={styles.select} className="form-input">
                    {crmOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Waar kunnen we je mee helpen?</label>
                  <textarea
                    name="bericht"
                    placeholder="Vertel kort over je situatie of stel je vraag..."
                    style={styles.textarea}
                    className="form-input"
                  />
                </div>

                <Button type="submit" size="large" showArrow style={{ width: '100%', justifyContent: 'center' }}>
                  Verstuur aanvraag
                </Button>
              </form>
            </>
          ) : (
            <div style={styles.successMessage}>
              <div style={styles.successIcon}>✓</div>
              <h3 style={styles.successTitle}>Bedankt voor je aanvraag!</h3>
              <p style={styles.successText}>
                We hebben je bericht ontvangen en nemen binnen 24 uur contact met je op
                om een intake gesprek in te plannen.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
